import Vue from 'vue';
import axios from 'axios';
import router from './router';
import store from './store';
import { IPager } from "@/models/Pager";

interface ListParams {
    page: number;
    take: number;
    sort: string;
    order: string;
    filters: string[];
    relations: string[];
}

interface ExportParams {
    sort: string;
    order: string;
    csv_export: number;
    filters: string[];
    relations: string[];
}

export default class Api {
    private url = store.getters.partnerApiUrl;
    private data: any;
    private pager: IPager = {
        count: 0,
        take: 0,
        page: 0,
        totalCount: 0
    };
    [key: string]: any;

    private defParams = {
        page: 1,
        take: 20,
        sort: 'id',
        order: 'DESC',
        filters: [],
        relations: [],
    };

    private defExportParams = {
        sort: 'id',
        order: 'ASC',
        csv_export: 1,
        filters: [],
        relations: [],
    };

    public async getApplications(params: object = {}) {
        const preparedParams: ListParams = {...this.defParams, ...params};

        await this.sendGetToApi(this.url + '/applications', preparedParams);

        return await this.data;
    }

    public async getApplication(id: string, params: object = {}) {
        await this.sendGetToApi(this.url + '/application/' + id, params);

        return await this.data.data;
    }

    public async changeApplicationStatus(id: string, status: string, message: string) {
        await axios.post(
            this.url + '/application/' + id + '/status',
            {status, message},
        ).then((response) => {
            this.setData(response);
            if (response.data.status === 'ok') {
                Vue.notify({group: 'info', type: 'success', text: 'Status bol zmenený'});
            } else {
                Vue.notify({group: 'info', type: 'warning', text: 'Status sa nezmenil'});
            }
        }).catch((error) => {
            this.notifyError(error.response.data.description);
        });

        return await this.data;
    }

    public async exportApplications(params: object = {}){
        const preparedParams: ExportParams = {...this.defExportParams, ...params};

        const data = await this.sendGetToApi(this.url + '/applications/export', preparedParams);
        return await this.data;
    }

    public async getUsers(params: object = {}) {
        const preparedParams: ListParams = {...this.defParams, ...params};

        await this.sendGetToApi(this.url + '/users', preparedParams);

        return await this.data;
    }

    public async getUser(id: string, params: object = {}) {
        await this.sendGetToApi(this.url + '/users/' + id, params);

        return await this.data.data;
    }

    public async getPartnerApplications(id: string, params: object = {}) {
        const preparedParams: ListParams = {...this.defParams, ...params};

        await this.sendGetToApi(this.url + '/partner/' + id + '/applications', preparedParams);

        return await this.data;
    }

    public async getPartners(params: object = {}) {
        const preparedParams: ListParams = {...this.defParams, ...params};

        await this.sendGetToApi(this.url + '/partners', preparedParams);

        return await this.data;
    }

    public async getPartner(id: string, params: object = {}) {
        await this.sendGetToApi(this.url + '/partners/' + id, params);

        return await this.data.data;
    }

    public async getPartnerCardNumbers(id: string, params: object = {}) {
        await this.sendGetToApi(`${this.url}/partner/${id}/card-numbers`, params);

        return await this.data;
    }

    public async editPartner(partnerId: string, data: {name: string, email: string}) {
        await axios.put(
            `${this.url}/partner/${partnerId}/edit`,
            data,
        ).then((response) => {
            if (response.status === 200) {
                Vue.notify({group: 'info', type: 'success', text: 'Zmenené'});

                if (store.getters.partner === partnerId) {
                    store.state.partnerName = data.name;
                }

                router.push('/partneri');
            }
        }).catch((error) => {
            this.notifyError(error.response.data.description);
        });
    }

    public async createPartner(data: {name: string, email: string}) {
        await axios.post(
            `${this.url}/partner/store`,
            data,
        ).then((response) => {
            if (response.status === 200) {
                Vue.notify({group: 'info', type: 'success', text: 'Partner vytvorený'});
                router.push('/partneri');
            }
        }).catch((error) => {
            this.notifyError(error.response.data.description);
        });
    }

    public async createUser(data: {email: string, role: string, partner: string}) {
        await axios.post(
            `${this.url}/user/create`,
            data,
        ).then((response) => {
            if (response.status === 200) {
                Vue.notify({group: 'info', type: 'success', text: 'Uživaťeľ vytvorený'});
                router.push('/uzivatelia');
            }
        }).catch((error) => {
            this.notifyError(error.response.data.description);
        });
    }

    public async editUser(userId: string, data: {email: string, role: string, partner: string}) {
        await axios.put(
            `${this.url}/user/${userId}/edit`,
            data,
        ).then((response) => {
            if (response.status === 200) {
                Vue.notify({group: 'info', type: 'success', text: 'Uživaťeľ uložený'});
                router.push('/uzivatelia');
            }
        }).catch((error) => {
            this.notifyError(error.response.data.description);
        });
    }

    public async deleteUser(userId: string) {
        // aby user nemohol seba vymazat
        // cez store.getters su tam niekedy neaktualne hodnoty z nejakeho dovodu
        const loggedInUserId = localStorage.getItem('userId');
        if (userId !== loggedInUserId) {
            await axios.delete(
                `${this.url}/user/${userId}/delete`
            ).then((response) => {
                if (response.status === 200) {
                    Vue.notify({group: 'info', type: 'success', text: 'Uživaťeľ zmazaný'});
                }
            }).catch((error) => {
                this.notifyError(error.response.data.description);
            });
        } else {
            this.notifyError('Nie je možné odstrániť vlastný účet.');
        }

        router.push('/uzivatelia');
    }

    public async postChangePassword(oldPassword: string, newPassword: string, newPasswordConfirmation: string) {
        await axios.post(
            this.url + '/users/change-password',
            {oldPassword, newPassword, newPasswordConfirmation},
        ).then((response) => {
            this.setData(response);
            if (response.data.status === 200) {
                Vue.notify({group: 'info', type: 'success', text: 'Heslo bolo zmenené.'});
            }
        }).catch((error) => {
            this.notifyError(error.response.data.description);
      });
    }

    public async postResetPassword(email: string) {
        await axios.post(
            this.url + '/reset-password',
            {email},
        ).then((response) => {
            if (response.data.status === 200) {
                Vue.notify({group: 'info', type: 'success', text: 'Link na obnovenie bol zaslaný na váš email'});
                router.push('/login');
            }
        }).catch((error) => {
            this.notifyError();
        });
    }

    public async postNewPassword(password: string, passwordValidate: string, token: string) {
        await axios.post(
            this.url + '/reset-password/' + token,
            {password, passwordValidate},
        ).then((response) => {
            if (response.data.status === 200) {
                Vue.notify({group: 'info', type: 'success', text: 'Heslo bolo zmenené.'});
            }
        }).catch((error) => {
            this.notifyError();
        });
    }

    public async postNewApplication(data: object) {
        await axios.post(
            this.url + '/application/store',
            data,
        ).then((response) => {
            this.setData(response);
            router.push('/formular/dakujeme');
        }).catch((error) => {
            let errMessage = 'Nastala chyba';
            if (error.response.data.message === 'REGISTERED_EMAIL') {
                errMessage = 'Žiadosť s týmto e-mailom už je zaregistrovaná.';
            }
            this.notifyError(errMessage);
        });
    }

    public async putPartnerCardRange(partnerId: string, data: object = {}) {
        await axios.put(
            `${this.url}/partner/${partnerId}/card-range`,
            data,
        ).then((response) => {
            if (response.data.status === 200) {
                Vue.notify({group: 'info', type: 'success', text: 'Zmenené'});
            }
        }).catch((error) => {
            this.notifyError(error.response.data.description);
        });
    }

    public async postImportPartnerCardNumbers(partnerId: string, data: any = {}) {
        const cardData = await this.readCardData(data.upload)

        await axios.post(
            `${this.url}/partner/${partnerId}/import-card-numbers`,
            { cardData: cardData }
        ).then((response) => {
            if (response.data.status === 200) {
                Vue.notify({group: 'info', type: 'success', text: 'Zmenené'});
            }
        }).catch((error) => {
            this.notifyError(error.response.data.description);
        });
    }

    public async getPublicPartner(id: string, params: object = {}) {
        await this.sendGetToApi(this.url + '/public/partners/' + id, params);

        return await this.data.data;
    }

    public async validateToken() {
        await this.checkIsTokenValid();
    }

    public async getPartnerStatusCounts() {
        let params = {};
        if (store.getters.role === 'partner') {
            params = {
                partnerId: store.getters.partner,
            };
        }
        await this.sendGetToApi(this.url + `/application/status-count`, params);

        return await this.data.data;
    }

    private setData(response: any) {
        this.data = response.data;
        localStorage.setItem('token', response.headers.token);
    }

    private notifyError(message: string = 'Nastala chyba') {
        Vue.notify({group: 'info', type: 'error', text: message});
    }

    private async sendGetToApi(url: string, params: any) {
        await axios.get(
            url,
            {params},
        ).then((response) => {
            this.setData(response);
        }).catch((error) => {
            this.checkIsTokenValid();
            router.push('/');
            this.notifyError();
        });
    }

    private async checkIsTokenValid() {
        await axios.get(this.url + '/me')
            .then().catch((error) => {
                store.dispatch('logout')
                    .then(() => router.push('/login'));
            });
    }

    private readCardData(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            const utf8 = require('utf8')
            const base64 = require('base-64')

            reader.onload = function () {
                try {
                    const bytes = utf8.encode(reader.result as string)
                    const cardData = base64.encode(bytes)
                    resolve(cardData)
                } catch (err) {
                    reject(err)
                }
            }

            reader.onerror = function () {
                reject(reader.error)
            }

            reader.readAsText(file)
        })
    }
}

export const ApiRepository: Api = new Api();
